import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

export interface AttsAttribute {
  id: number;
  created_at: string;
  updated_at: string;
  global_set_id: number;
  frontend_name: string;
  channel_attribute_id: string;
  value_type: string;
  is_mappable: number;
  allow_free_values: number;
  channel_priority: string | null;
  channel_attribute_group: string | null;
  parent_only: number;
  recommendation_level: string;
  attribute_classification: string;
  attribute_type: string;
  channel_value_type: string;
  min_occurs: number;
  max_occurs: number;
  min_items: number;
  documentation: string | null;
  hash_identifier: string | null;
  channel_attribute_name: string | null;
  getter_method: string | null;
  validator_method: string | null;
  patch_path: string | null;
  channel_attribute_level: string | null;
  ignore_validation: number;
  commonality_key: string | null;
  source_meta_json: Record<string, unknown> | null;
}

export interface AttsAssignment {
  id: number;
  created_at: string;
  updated_at: string;
  attribute_id: number;
  grouping_id: number;
  containing_structure_id: number;
  value_type: string | null;
  is_mappable: number;
  allow_free_values: number;
  channel_priority: string | null;
  channel_attribute_group: string | null;
  parent_only: string | null;
  child_only?: string | null;
  recommendation_level: string;
  channel_value_type: string;
  min_occurs: number;
  max_occurs: number;
  min_items: number | null;
  documentation: string | null;
  hash_identifier: string | null;
  global_set_id: number;
  ignore_validation: number;
  source_meta_json: Record<string, unknown> | null;
  max_items: number | null;
  max_length: number | null;
  min_length: number | null;
  min_value: number | null;
  max_value: number | null;
  can_vary: boolean | 0 | 1 | null;
  /** Marketplace variation theme this assignment drives when it varies (e.g. "Size Group"). */
  variation_theme_name?: string | null;
  atts_attribute: AttsAttribute;
  atts_grouping?: { global_set?: { astur_integration_id?: number | null } | null } | null;
}

/**
 * Whether an ATTS assignment can vary across variants.
 *
 * The API serializes `can_vary` as a boolean, a number, or a string
 * (`true` / `1` / `'1'`), so callers must coerce with `Number(...)` before comparing.
 * Centralized here so the "Variable" affordances — the expanded chip/alert and
 * the collapsed-row CallSplitIcon (card #3452) — and their tests share one
 * predicate instead of re-implementing the comparison. Null/undefined coerce to
 * `false` (`Number(null) === 0`, `Number(undefined) === NaN`).
 */
export const canVary = (assignment?: Partial<Pick<AttsAssignment, 'can_vary'>> | null): boolean => Number(assignment?.can_vary) === 1;

// Staged rollout: the variation affordances (header summary, name-column chip, row
// stripe) only show for these Astur integration ids. Others keep the legacy indicators.
const VARIATION_ROLLOUT_INTEGRATION_IDS = [2];

// can_vary attributes that must not be presented as variation axes, keyed by the
// global set's Astur integration id (2 = Target).
const NON_VARIATION_ATTRIBUTE_IDS_BY_INTEGRATION: Record<number, number[]> = { 2: [294488, 294504] };

/**
 * `canVary`, limited to rolled-out integrations and minus their excluded attributes.
 *
 * The integration comes from the assignment's own global set when the spec
 * response includes that relation, otherwise from `fallbackIntegrationId` (the
 * posting template's integration), so an omitted relation never silently
 * disables the rollout.
 */
export const isVariationAxis = (
  assignment?: Partial<Pick<AttsAssignment, 'can_vary' | 'attribute_id' | 'atts_grouping'>> | null,
  fallbackIntegrationId?: number | null
): boolean => {
  if (!canVary(assignment)) return false;
  const integrationId = Number(assignment?.atts_grouping?.global_set?.astur_integration_id ?? fallbackIntegrationId);
  return (
    VARIATION_ROLLOUT_INTEGRATION_IDS.includes(integrationId) &&
    !(NON_VARIATION_ATTRIBUTE_IDS_BY_INTEGRATION[integrationId] ?? []).includes(Number(assignment?.attribute_id))
  );
};

export interface AttributeComponent {
  assignment_id: number;
  attribute_path: string;
  frontend_name: string;
  value_type: string;
  is_required: boolean;
  min_occurs: number;
  max_occurs: number;
  recommendation_level: string;
  documentation: string | null;
}

export interface ContainerComponent {
  assignment_id: number;
  container_path: string;
  frontend_name: string;
  min_occurs: number;
  max_occurs: number;
  containerComponents?: ContainerComponent[];
  attributeComponents?: AttributeComponent[];
}

export interface AttributeStructure {
  containerComponents: ContainerComponent[];
}

export interface AttributeSpecResponse {
  assignment_by_id: Record<number, AttsAssignment>;
  structure: AttributeStructure;
}

export const getAttributeSpec = async (
  scope: RequestScope,
  subscriptionId: number,
  groupingId: number,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<AttributeSpecResponse>> => {
  try {
    if (!subscriptionId || subscriptionId < 1) {
      throw new Error('Valid subscription ID is required');
    }
    if (!groupingId || groupingId < 1) {
      throw new Error('Valid grouping ID is required');
    }

    const result = await scopedChannelApi(scope).get<AttributeSpecResponse | { data?: AttributeSpecResponse }>(`atts/spec/${groupingId}`, {
      ...options,
      headers: {
        'x-astur-subscription-id': subscriptionId.toString(),
      },
    });

    // The `in` check needs an object guard: a null/204 body must surface the
    // clean missing-data error below, not a TypeError.
    const responseData = result.data;
    const attributeSpec =
      responseData && typeof responseData === 'object' && 'assignment_by_id' in responseData
        ? responseData
        : (responseData as { data?: AttributeSpecResponse } | null | undefined)?.data;
    if (!attributeSpec) {
      throw new Error('Attribute specification response did not include data');
    }

    return {
      data: attributeSpec,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
